import {Text, Stack, Group, UnstyledButton} from "@mantine/core";
import {useTranslation} from "react-i18next";
import IconChevronDown from "../../../../assets/chevron-down.svg?react";
import {DirectSearchResult} from "../../../../types";
import {
    buildFileDownloadHref,
    fileExtensionMap,
    isValidFileType,
} from "../../../content/contentUtils";
import {ResourceType} from "../../../content/types";
import {resolveTypeLabel} from "../../../content/typeIcons";
import classes from "./SearchResultsRenderer.module.css";

interface ResultRowProps {
    result: DirectSearchResult;
    index: number;
    showBorder: boolean;
    onClick?: () => void;
}

export function ResultRow({result, showBorder, onClick}: ResultRowProps) {
    const {t} = useTranslation();
    const meta = result.metadata;
    const resourceTypeId = meta?.resourceTypeid as
        | ResourceType
        | undefined;
    const topicId = meta?.topicId;
    const isFile = isValidFileType(resourceTypeId) && Boolean(topicId);

    const href = isFile ? buildFileDownloadHref(topicId!) : result.url;
    const extension = isFile
        ? fileExtensionMap[String(resourceTypeId)] ?? ""
        : "";

    const badgeLabel = resolveTypeLabel(t, meta?.topicType, result.url);
    const subText = result.description || result.snippet || badgeLabel;

    const anchorProps = isFile
        ? {
              download: `${result.title}${extension}`,
              "data-pw": "file-download" as const,
          }
        : {
              target: "_blank" as const,
              rel: "noopener noreferrer" as const,
          };

    return (
        <UnstyledButton
            className={`${classes.resultRow} ${showBorder ? classes.resultRowBorder : ""}`}
            component="a"
            href={href}
            onClick={onClick}
            {...anchorProps}
        >
            <Group wrap="nowrap" gap="md" align="center">
                <Stack gap={2} style={{flex: 1, minWidth: 0}}>
                    <Text size="sm" className={classes.resultTitle}>
                        {result.title}
                    </Text>
                    <Text size="xs" c="dimmed" lineClamp={2}>
                        {subText}
                    </Text>
                </Stack>
                <span className={classes.typeBadge}>{badgeLabel}</span>
                <IconChevronDown
                    width={12}
                    height={12}
                    className={classes.chevronRight}
                />
            </Group>
        </UnstyledButton>
    );
}
